import type { FileNode } from './fileTreeUtils';

/**
 * Format file size in bytes to human readable format
 */
export const formatFileSize = (bytes: number): string => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Bytes';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const formattedSize = Math.round((bytes / Math.pow(1024, i)) * 100) / 100;
  return `${formattedSize} ${sizes[i]}`;
};

/**
 * Recursively sum file sizes under a tree node (files + nested folders).
 */
export const getFolderSize = (node: FileNode): number => {
  if (node.type === 'file') {
    return node.size || 0;
  }

  return (node.children ?? []).reduce(
    (sum, child) => sum + getFolderSize(child),
    0
  );
};

/**
 * Format date to locale string
 */
export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString();
};

/**
 * Format date to short format (just date)
 */
export const formatDateShort = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString();
};

/**
 * Format date with time but without seconds, with leading zeros for alignment
 * Format: MM/DD/YYYY, HH:MM AM/PM
 */
export const formatDateWithTime = (dateString: string): string => {
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const displayHoursStr = String(displayHours).padStart(2, '0');
  return `${month}/${day}/${year}, ${displayHoursStr}:${minutes} ${ampm}`;
};

/**
 * Format date with leading zeros for alignment
 * Format: MM/DD/YYYY
 */
export const formatDateShortPadded = (dateString: string): string => {
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

/**
 * Get file extension from filename
 */
export const getFileExtension = (filename: string): string => {
  return filename.split('.').pop()?.toLowerCase() || '';
};

import { DAWType, DAW_EXTENSIONS } from '../../types/daw';
import {
  isDAWFile as isDAWFileUtil,
  getDAWTypeFromExtension,
} from './dawUtils';
import { FileCategory, FILE_EXTENSIONS, IGNORED_FILES } from '../models/enums';

/**
 * Check if a file should be ignored based on system files and ignored patterns
 * @param fileName - Full file path or just filename
 * @returns true if the file should be ignored
 */
export const shouldIgnoreFile = (fileName: string): boolean => {
  const fileNameOnly = fileName.split('/').pop() || fileName;

  return IGNORED_FILES.some(
    (ignored) =>
      fileNameOnly === ignored || fileNameOnly.startsWith(ignored + '.')
  );
};

// Common file type mappings
const CONTENT_TYPE_MAP: Record<string, string> = {
  // Audio files
  '.mp3': 'audio/mpeg',
  '.wav': 'audio/wav',
  '.aiff': 'audio/aiff',
  '.flac': 'audio/flac',
  '.ogg': 'audio/ogg',
  '.m4a': 'audio/mp4',

  // Video files
  '.mp4': 'video/mp4',
  '.mov': 'video/quicktime',
  '.avi': 'video/x-msvideo',

  // Images
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.gif': 'image/gif',
  '.bmp': 'image/bmp',
  '.svg': 'image/svg+xml',

  // Documents
  '.pdf': 'application/pdf',
  '.txt': 'text/plain',
  '.doc': 'application/msword',
  '.docx':
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',

  // Archives
  '.zip': 'application/zip',
  '.rar': 'application/x-rar-compressed',
  '.7z': 'application/x-7z-compressed',
  '.tar': 'application/x-tar',
  '.gz': 'application/gzip',

  // Default for unknown extensions
  '': 'application/octet-stream',
};

export function getContentType(file: File): string {
  const fileName = file.name.toLowerCase();
  const extension = fileName.substring(fileName.lastIndexOf('.'));

  // Check DAW files first
  if (DAW_EXTENSIONS[extension]) {
    return DAW_EXTENSIONS[extension].contentType;
  }

  // Check common file types
  return CONTENT_TYPE_MAP[extension] || CONTENT_TYPE_MAP[''];
}

/**
 * Get file type category based on content type or extension
 */
export const getFileCategory = (
  contentType: string,
  filename?: string
): FileCategory => {
  const type = contentType.toLowerCase();

  if (type.startsWith('image/')) return FileCategory.IMAGE;
  if (type.startsWith('video/')) return FileCategory.VIDEO;
  if (type.startsWith('audio/')) return FileCategory.AUDIO;
  if (type.startsWith('text/') || type.includes('document'))
    return FileCategory.DOCUMENT;
  if (type.includes('pdf')) return FileCategory.PDF;
  if (type.includes('zip') || type.includes('compress'))
    return FileCategory.ARCHIVE;

  // Fallback to extension-based detection
  if (filename) {
    const ext = getFileExtension(filename);
    if (FILE_EXTENSIONS.IMAGE.includes(ext as any)) return FileCategory.IMAGE;
    if (FILE_EXTENSIONS.VIDEO.includes(ext as any)) return FileCategory.VIDEO;
    if (FILE_EXTENSIONS.AUDIO.includes(ext as any)) return FileCategory.AUDIO;
    if (ext === 'pdf') return FileCategory.PDF;
    if (FILE_EXTENSIONS.ARCHIVE.includes(ext as any))
      return FileCategory.ARCHIVE;
    if (FILE_EXTENSIONS.DOCUMENT.includes(ext as any))
      return FileCategory.DOCUMENT;
    // DAW files
    if (
      [
        'als',
        'logicx',
        'ptf',
        'ptx',
        'cpr',
        'flp',
        'rpp',
        'song',
        'reason',
      ].includes(ext)
    )
      return FileCategory.DAW;
  }

  return FileCategory.FILE;
};

/**
 * Download file from URL
 */
export const downloadFile = (url: string, filename: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Validate file size
 */
export const validateFileSize = (
  file: File,
  maxSizeMB: number = 100
): boolean => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

/**
 * Validate file type
 */
export const validateFileType = (
  file: File,
  allowedTypes: string[]
): boolean => {
  return allowedTypes.some((type) => file.type.includes(type));
};

/**
 * Generate unique file ID
 */
export const generateFileId = (): string => {
  return Math.random().toString(36).substr(2, 9);
};

/**
 * Extract folder name from file path
 */
export const getFolderName = (filePath: string): string => {
  const parts = filePath.split('/');
  return parts.length > 1 ? parts[0] : '';
};

/**
 * Get relative path within a folder
 */
export const getRelativePathInFolder = (filePath: string): string => {
  const parts = filePath.split('/');
  return parts.slice(1).join('/') || parts[0];
};

/**
 * Check if file is in a folder
 */
export const isInFolder = (filePath: string): boolean => {
  return filePath.includes('/');
};

interface FileInfo {
  fileName: string;
  contentType: string;
  size: number;
  uploadedAt: string;
  [key: string]: string | number;
}

interface FileGroup {
  [folder: string]: FileInfo[];
}

/**
 * Group files by folder structure
 */
export const groupFilesByFolder = (files: FileInfo[]): FileGroup => {
  const grouped: FileGroup = {
    'Root Files': [],
  };

  files.forEach((file) => {
    if (isInFolder(file.fileName)) {
      const folderName = getFolderName(file.fileName);
      if (!grouped[folderName]) {
        grouped[folderName] = [];
      }
      grouped[folderName].push(file);
    } else {
      grouped['Root Files'].push(file);
    }
  });

  // Remove empty root files group
  if (grouped['Root Files'].length === 0) {
    delete grouped['Root Files'];
  }

  return grouped;
};

/**
 * Check if this is a DAW project file
 * Uses shared utility from dawUtils
 */
export const isDAWProject = (filename: string): boolean => {
  return isDAWFileUtil(filename);
};

/**
 * Map DAWType enum to display name
 */
const DAW_DISPLAY_NAMES: Record<DAWType, string> = {
  [DAWType.ABLETON]: 'Ableton Live',
  [DAWType.LOGIC]: 'Logic Pro X',
  [DAWType.PRO_TOOLS]: 'Pro Tools',
  [DAWType.CUBASE]: 'Cubase',
  [DAWType.FL_STUDIO]: 'FL Studio',
  [DAWType.REAPER]: 'REAPER',
  [DAWType.STEMS]: 'Stems',
  [DAWType.UNKNOWN]: 'Unknown DAW',
};

/**
 * Get DAW type display name from filename
 * Uses shared utility from dawUtils for type detection
 */
export const getDAWType = (filename: string): string => {
  const ext = getFileExtension(filename);
  const extension = ext ? `.${ext}` : '';
  const dawType = getDAWTypeFromExtension(extension);
  return DAW_DISPLAY_NAMES[dawType];
};

/**
 * Get DAW logo path from file extension or DAW name
 */
export const getDAWLogo = (input: string): string | null => {
  let dawType: string;

  // If input looks like a filename, get DAW type from it
  if (input.includes('.')) {
    dawType = getDAWType(input);
  } else {
    dawType = input;
  }

  const logoMap: { [key: string]: string | null } = {
    'Ableton Live': '/dawLogos/logo/Ableton_full.png',
    'FL Studio': '/dawLogos/logo/FLStudio_full.png',
    // Add more logos when available
    'Logic Pro X': null, // "/dawLogos/logicLogo.png",
    'Pro Tools': null, // "/dawLogos/proToolsLogo.png",
    Cubase: null, // "/dawLogos/cubaseLogo.png",
    REAPER: null, // "/dawLogos/reaperLogo.png",
    'Studio One': null, // "/dawLogos/studioOneLogo.png",
    Reason: null, // "/dawLogos/reasonLogo.png",
  };

  return logoMap[dawType] || null;
};

/**
 * Get DAW color theme for consistent styling
 */
export const getDAWColor = (input: string): string => {
  let dawType: string;

  // If input looks like a filename, get DAW type from it
  if (input.includes('.')) {
    dawType = getDAWType(input);
  } else {
    dawType = input;
  }

  const colorMap: { [key: string]: string } = {
    'Ableton Live': '#4a90e2',
    'FL Studio': '#7b68ee',
    'Logic Pro X': '#007acc',
    'Pro Tools': '#9013fe',
    Cubase: '#00c853',
    REAPER: '#d32f2f',
    'Studio One': '#1976d2',
    Reason: '#e91e63',
  };

  return colorMap[dawType] || '#666';
};

/**
 * Get DAW information including type, logo, and color
 */
export const getDAWInfo = (filename: string) => {
  const dawType = getDAWType(filename);
  const logo = getDAWLogo(filename);
  const color = getDAWColor(filename);

  return {
    type: dawType,
    logo,
    color,
    hasLogo: logo !== null,
  };
};

export function detectPrimaryDAW(
  files: Array<{
    fileName: string;
    contentType: string;
    size: number;
    uploadedAt: string;
  }>
): { type: DAWType } | null {
  if (!files || files.length === 0) {
    return null;
  }

  // Count DAW files by type
  const dawCounts: Record<DAWType, number> = {
    [DAWType.ABLETON]: 0,
    [DAWType.LOGIC]: 0,
    [DAWType.PRO_TOOLS]: 0,
    [DAWType.CUBASE]: 0,
    [DAWType.FL_STUDIO]: 0,
    [DAWType.REAPER]: 0,
    [DAWType.STEMS]: 0,
    [DAWType.UNKNOWN]: 0,
  };

  files.forEach((file) => {
    const fileName = file.fileName.toLowerCase();
    const extension = fileName.substring(fileName.lastIndexOf('.'));

    if (DAW_EXTENSIONS[extension]) {
      dawCounts[DAW_EXTENSIONS[extension].type]++;
    }
  });

  // Find the DAW type with the most files
  let maxCount = 0;
  let primaryDAW: DAWType | null = null;

  Object.entries(dawCounts).forEach(([dawType, count]) => {
    if (count > maxCount) {
      maxCount = count;
      primaryDAW = dawType as DAWType;
    }
  });

  if (primaryDAW) {
    return { type: primaryDAW };
  }

  // No DAW files found — check if all uploads are audio files (stems project)
  const hasAudioFiles = files.some((f) => f.contentType.startsWith('audio/'));
  if (hasAudioFiles) {
    return { type: DAWType.STEMS };
  }

  return null;
}

export async function calculateHashes(file: File | Blob): Promise<{
  quickHash: string;
  fullHash: string;
}> {
  // Quick hash (first 10KB)
  const quickHashBuffer = await file.slice(0, 10240).arrayBuffer();
  const quickHashArray = new Uint8Array(
    await crypto.subtle.digest('SHA-256', quickHashBuffer)
  );
  const quickHash = Array.from(quickHashArray)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  // Full hash
  const fullHashBuffer = await file.arrayBuffer();
  const fullHashArray = new Uint8Array(
    await crypto.subtle.digest('SHA-256', fullHashBuffer)
  );
  const fullHash = Array.from(fullHashArray)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  return { quickHash, fullHash };
}

export async function getFileHandle(file: File): Promise<FileSystemFileHandle> {
  // Request file system access
  const dirHandle = await window.showDirectoryPicker({
    mode: 'readwrite',
  });

  // Create or get file handle
  return await dirHandle.getFileHandle(file.name, { create: true });
}
